import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { NotificationService } from '@app/core/services/notification.service';
import { MobileContextService } from '@core/context/services/mobile-context.service';
import { DateUtils } from '@shared/date-utils';
import { ContextoPWA } from '@shared/types';
import {
  AcompanhamentoAluno,
  AcompanhamentoComponenteCurricular,
  AcompanhamentoFiltros,
  AcompanhamentoFormulario,
  AcompanhamentoOrdenacaoOption,
  AcompanhamentoPedagogicoRegistro,
  AcompanhamentoPeriodo
} from '../models/acompanhamento-pedagogico.types';
import { PeriodoSelectOption } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';
import { AcompanhamentoPedagogicoService } from '../services/acompanhamento-pedagogico.service';
import { AcompanhamentoPedagogicoApiService } from '../services/acompanhamento-pedagogico-api.service';
import { AcompanhamentoFormComponent } from './acompanhamento-form.component';
import {
  aplicarOrdenacaoRegistros,
  camposObrigatoriosPreenchidos,
  getContextoKey,
  getDescricaoPeriodoOption,
  getFormularioInicial,
  getMensagemConfirmacaoDespublicacao,
  getMensagemConfirmacaoExclusao,
  getVinculoDisciplina,
  isDataFiltroValida,
  mesclarAlunosSelecionados,
  normalizarFormulario,
  serializarFormulario,
  temAlteracoesFormularioNaoSalvas,
  validarFormulario
} from '../utils/acompanhamento-pedagogico.utils';


@Component({
  selector: 'app-acompanhamento-pedagogico',
  templateUrl: './acompanhamento-pedagogico.component.html',
  styleUrls: ['./acompanhamento-pedagogico.component.scss']
})
export class AcompanhamentoPedagogicoComponent implements OnInit, OnDestroy {
  readonly limiteObservacao = 32000;

  @ViewChild('formRef') private formRef?: AcompanhamentoFormComponent;

  private destroy$ = new Subject<void>();
  private filtroChange$ = new Subject<void>();
  private searchId = 0;
  private formularioSnapshotInicial = '';
  private ultimoContextoCarregadoKey: string | null = null;
  private _alunoIdsForçadosSelecionados = new Set<number>();
  private preservarSelecaoAlunosNoProximoCarregamento = false;

  contexto: ContextoPWA | null = null;
  semContexto = false;
  carregando = false;
  carregandoAlunos = false;
  carregandoComponentes = false;
  carregandoPeriodos = false;
  carregandoInicialModal = false;
  carregandoConteudoCard = false;
  salvandoFormulario = false;

  periodos: AcompanhamentoPeriodo[] = [];
  periodoSelecionado: AcompanhamentoPeriodo | null = null;
  componentes: AcompanhamentoComponenteCurricular[] = [];
  alunos: AcompanhamentoAluno[] = [];
  registrosOriginais: AcompanhamentoPedagogicoRegistro[] = [];
  registros: AcompanhamentoPedagogicoRegistro[] = [];

  termoAluno = '';
  filtroData = '';
  filtroDataSelecionada: Date | null = null;
  ordenacaoDropdownAberta = false;
  formularioAberto = false;
  somentePublicoAlvo = false;
  mostrarErrosFormulario = false;
  carregandoCampoAdicional = false;
  destacarErroAlunosFormulario = false;

  campoAdicional: any = null;

  errosFormulario: string[] = [];

  formulario: AcompanhamentoFormulario = getFormularioInicial();
  confirmacao: {
    aberta: boolean;
    tipo: 'despublicar' | 'excluir' | 'descartar-formulario';
    titulo: string;
    mensagem: string;
    registro: AcompanhamentoPedagogicoRegistro | null;
  } = {
    aberta: false,
    tipo: 'despublicar',
    titulo: '',
    mensagem: '',
    registro: null
  };

  readonly opcoesOrdenacao: AcompanhamentoOrdenacaoOption[] = [
    { id: 'disciplina-asc', label: 'Disciplina A-Z', campo: 'disciplina', direcao: 'asc' },
    { id: 'disciplina-desc', label: 'Disciplina Z-A', campo: 'disciplina', direcao: 'desc' },
    { id: 'aluno-asc', label: 'Aluno A-Z', campo: 'aluno', direcao: 'asc' },
    { id: 'aluno-desc', label: 'Aluno Z-A', campo: 'aluno', direcao: 'desc' },
    { id: 'data-asc', label: 'Data crescente', campo: 'data', direcao: 'asc' },
    { id: 'data-desc', label: 'Data decrescente', campo: 'data', direcao: 'desc' },
    { id: 'observacao-asc', label: 'Observação A-Z', campo: 'observacao', direcao: 'asc' },
    { id: 'observacao-desc', label: 'Observação Z-A', campo: 'observacao', direcao: 'desc' }
  ];
  ordenacaoSelecionada = this.opcoesOrdenacao[0];

  constructor(
    private acompanhamentoService: AcompanhamentoPedagogicoService,
    private apiService: AcompanhamentoPedagogicoApiService,
    private mobileContextService: MobileContextService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.somentePublicoAlvo = this.acompanhamentoService.loadPreferencias().filtrarPublicoAlvo;

    const contextoInicial = this.mobileContextService.getContextoAtual();
    this.ultimoContextoCarregadoKey = getContextoKey(contextoInicial);
    this.handleContextChange(contextoInicial);

    this.mobileContextService.contexto$
      .pipe(takeUntil(this.destroy$))
      .subscribe((contexto) => {
        const contextoKey = getContextoKey(contexto);

        if (contextoKey === this.ultimoContextoCarregadoKey) {
          return;
        }

        this.ultimoContextoCarregadoKey = contextoKey;
        this.handleContextChange(contexto);
      });

    this.filtroChange$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => this.carregarRegistros('conteudo'));
  }

  ngOnDestroy(): void {
    this.atualizarBloqueioScroll(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.confirmacao.aberta) {
      this.fecharConfirmacao();
      return;
    }

    if (this.formularioAberto) {
      this.fecharFormulario();
      return;
    }

    this.ordenacaoDropdownAberta = false;
  }

  get carregandoTela(): boolean {
    return !this.semContexto && (this.carregandoInicialModal || this.carregandoComponentes || this.carregandoPeriodos);
  }

  get alunosElegiveis(): AcompanhamentoAluno[] {
    return this.alunos.filter((aluno) => {
      const alunoSelecionado = this.formulario.alunoIds.includes(aluno.id);

      if (!aluno.ativo) {
        return false;
      }

      if (this.somentePublicoAlvo && !this.isAlunoComDeficiencia(aluno) && !alunoSelecionado) {
        return false;
      }

      if (this.isTurmaAtividadeOuModularContexto && this.formulario.componenteId && aluno.disciplinas.length) {
        const vinculo = getVinculoDisciplina(aluno, this.formulario.componenteId);
        if (!vinculo || !vinculo.ativo || vinculo.dispensado) {
          return false;
        }
      }

      return true;
    });
  }

  get tituloFormularioModal(): string {
    return this.formulario.id ? 'Editando acompanhamento pedagógico' : 'Adicionando acompanhamento pedagógico';
  }

  get maxDateFormulario(): Date {
    return this._hoje;
  }

  readonly _hoje = new Date();

  private get isTurmaAtividadeOuModularContexto(): boolean {
    const turma = (this.contexto?.turma || {}) as any;
    const tiposAtividade = ['ATIVIDADE_COMPLEMENTAR', 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'];
    return tiposAtividade.includes(String(turma?.tipoAtendimento || '')) || !!turma?.etapaMatriz?.modular;
  }

  get isTurmaEjaModular(): boolean {
    const turma = (this.contexto?.turma || {}) as any;
    return !!turma?.etapaMatriz?.modular || String(turma?.tipoAtendimento || '').toLowerCase().includes('eja');
  }

  get formularioValidoParaSalvar(): boolean {
    return camposObrigatoriosPreenchidos({
      formularioAberto: this.formularioAberto,
      formulario: this.formulario,
      carregandoAlunos: this.carregandoAlunos,
      campoAdicional: this.campoAdicional
    });
  }

  periodoOptions: PeriodoSelectOption[] = [];

  toggleOrdenacaoDropdown(): void {
    this.ordenacaoDropdownAberta = !this.ordenacaoDropdownAberta;
  }

  selecionarPeriodo(periodo: AcompanhamentoPeriodo): void {
    this.periodoSelecionado = periodo;
    this.carregarRegistros('conteudo');
  }

  onPeriodoOptionSelected(option: PeriodoSelectOption): void {
    const periodo = this.periodos.find((item) => item.id === option.id);
    if (periodo) {
      this.selecionarPeriodo(periodo);
    }
  }

  selecionarOrdenacao(ordenacao: AcompanhamentoOrdenacaoOption): void {
    this.ordenacaoSelecionada = ordenacao;
    this.ordenacaoDropdownAberta = false;
    this.aplicarOrdenacao();
  }

  onFiltroChange(): void {
    this.filtroChange$.next();
  }

  onFiltroDataChange(data: Date | null): void {
    this.filtroDataSelecionada = data;
    if (data) {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const dia = String(data.getDate()).padStart(2, '0');
      this.filtroData = `${ano}-${mes}-${dia}`;
    } else {
      this.filtroData = '';
    }
    this.onFiltroChange();
  }

  adicionarAcompanhamento(): void {
    const formulario = getFormularioInicial();
    formulario.data = DateUtils.formatarDataISO(new Date());

    if (this.componentes.length === 1) {
      formulario.componenteId = this.componentes[0].id;
    }
    
    this.abrirFormulario(formulario);
  }

  editarRegistro(registro: AcompanhamentoPedagogicoRegistro): void {
    this.abrirFormulario({
      id: registro.id,
      componenteId: registro.componenteId,
      data: registro.data,
      observacao: registro.observacao,
      alunoIds: [registro.alunoId],
      publicar: registro.publicado,
      arquivos: registro.arquivos ?? []
    }, registro.campoAdicional || null);
  }

  excluirRegistro(registro: AcompanhamentoPedagogicoRegistro): void {
    this.abrirConfirmacao(
      'excluir',
      'Confirmar exclusão',
      getMensagemConfirmacaoExclusao(registro),
      registro
    );
  }

  excluirDoFormulario(): void {
    const registro = this.registros.find(r => r.id === this.formulario.id);
    if (registro) {
      this.fecharFormulario();
      this.excluirRegistro(registro);
    }
  }

  togglePublicacao(registro: AcompanhamentoPedagogicoRegistro): void {
    if (registro.publicado) {
      this.abrirConfirmacao(
        'despublicar',
        'Desfazer publicação',
        getMensagemConfirmacaoDespublicacao(registro),
        registro
      );
      return;
    }

    this.acompanhamentoService.atualizarPublicacao(this.contexto, registro.id, true, this.periodoSelecionado?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((registros) => {
        this.registrosOriginais = registros;
        this.registros = registros;
        this.notificationService.success({
          title: '',
          message: 'Acompanhamento pedagógico publicado com sucesso.',
          duration: 8000,
          position: 'top',
          hideTitle: true
        });
      });
  }

  onSomentePublicoAlvoChange(valor: boolean): void {
    this.somentePublicoAlvo = valor;
    this.acompanhamentoService.savePreferencias({ filtrarPublicoAlvo: this.somentePublicoAlvo });
    // Clear forced selection when public target filter changes, as eligible alunos list will change
    this._alunoIdsForçadosSelecionados.clear();
    this.preservarSelecaoAlunosNoProximoCarregamento = false;
    this.carregarAlunosElegiveis();
  }

  selecionarComponente(componenteId: number | null): void {
    this.formulario.componenteId = componenteId;
    this._alunoIdsForçadosSelecionados.clear();
    this.preservarSelecaoAlunosNoProximoCarregamento = false;

    if (!componenteId) {
      this.formulario.alunoIds = [];
    }

    this.carregarAlunosElegiveis();
  }

  onDataFormularioChange(data: string): void {
    this.formulario.data = data;
    this._alunoIdsForçadosSelecionados.clear();
    this.preservarSelecaoAlunosNoProximoCarregamento = false;
    this.carregarAlunosElegiveis();
  }

  onAlunoIdsFormularioChange(alunoIds: number[]): void {
    this.formulario.alunoIds = alunoIds;
    this.destacarErroAlunosFormulario = false;
  }

  fecharFormulario(forcar = false): void {
    if (!forcar && temAlteracoesFormularioNaoSalvas(this.formularioAberto, this.formulario, this.formularioSnapshotInicial)) {
      this.confirmacao = {
        aberta: true,
        tipo: 'descartar-formulario',
        titulo: 'Descartar alterações?',
        mensagem: 'Há alterações não salvas neste acompanhamento. Se continuar, as mudanças serão perdidas.',
        registro: null
      };
      return;
    }

    this.formularioAberto = false;
    this.mostrarErrosFormulario = false;
    this.campoAdicional = null;
    this.formulario = getFormularioInicial();
    this.formularioSnapshotInicial = '';
    this.carregandoCampoAdicional = false;
    this.errosFormulario = [];
    this._alunoIdsForçadosSelecionados.clear();
    this.preservarSelecaoAlunosNoProximoCarregamento = false;
    this.destacarErroAlunosFormulario = false;
    this.atualizarBloqueioScroll(false);
  }

  salvarFormulario(): void {
    if (this.salvandoFormulario) {
      return;
    }

    this.mostrarErrosFormulario = true;
    const erros = validarFormulario({
      formulario: this.formulario,
      alunos: this.alunos,
      limiteObservacao: this.limiteObservacao,
      carregandoCampoAdicional: this.carregandoCampoAdicional,
      campoAdicional: this.campoAdicional
    });
    this.errosFormulario = erros;

    if (erros.length) {
      this.notificationService.warning({
        title: 'Revise o formulário',
        message: erros[0]
      });
      return;
    }

    this.salvandoFormulario = true;

    this.formulario.arquivos = this.formRef?.arquivosAtuais ?? [];

    this.acompanhamentoService.salvarRegistro(
      this.contexto,
      this.formulario,
      this.periodoSelecionado?.id || 0,
      this.componentes,
      this.alunos,
      this.campoAdicional
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.salvandoFormulario = false;
        })
      )
      .subscribe((registros) => {
        this.registrosOriginais = registros;
        this.registros = registros;
        this.notificationService.success({
          title: '',
          message: this.formulario.id
            ? 'Acompanhamento pedagógico atualizado com sucesso.'
            : 'Acompanhamento pedagógico salvo com sucesso.',
          duration: 8000,
          position: 'top',
          hideTitle: true
        });
        this.fecharFormulario(true);
      });
  }

  confirmarAcao(): void {
    const registro = this.confirmacao.registro;
    if (!registro) {
      this.fecharConfirmacao();
      return;
    }

    if (this.confirmacao.tipo === 'despublicar') {
      this.acompanhamentoService.atualizarPublicacao(this.contexto, registro.id, false, this.periodoSelecionado?.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((registros) => {
          this.registrosOriginais = registros;
          this.registros = registros;
          this.notificationService.success({
            title: '',
            message: 'Publicação do acompanhamento pedagógico desfeita com sucesso.',
            duration: 8000,
            position: 'top',
            hideTitle: true
          });
          this.fecharConfirmacao();
        });
      return;
    }

    this.confirmarExclusao(registro);
  }

  salvarAntesDeFecharFormulario(): void {
    this.fecharConfirmacao();
    this.salvarFormulario();
  }

  descartarFormularioSemSalvar(): void {
    this.fecharConfirmacao();
    this.fecharFormulario(true);
  }

  fecharConfirmacao(): void {
    this.confirmacao = {
      aberta: false,
      tipo: 'despublicar',
      titulo: '',
      mensagem: '',
      registro: null
    };
  }

  private handleContextChange(contexto: ContextoPWA | null): void {
    this.contexto = contexto;
    this.semContexto = !contexto?.turma?.id;

    if (this.semContexto) {
      this.fecharFormulario(true);
      this.periodos = [];
      this.periodoOptions = [];
      this.componentes = [];
      this.alunos = [];
      this.periodoSelecionado = null;
      this.registrosOriginais = [];
      this.registros = [];
      this.carregando = false;
      this.carregandoComponentes = false;
      this.carregandoPeriodos = false;
      this.carregandoInicialModal = false;
      this.carregandoConteudoCard = false;
      return;
    }

    this.carregandoInicialModal = true;
    this.carregandoConteudoCard = false;
    this.carregandoComponentes = true;

    this.acompanhamentoService.getComponentes(this.contexto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.carregandoComponentes = false; })
      )
      .subscribe((componentes) => {
        this.componentes = componentes;

        if (componentes.length === 1) {
          this.selecionarComponente(componentes[0].id);
        }
      }, () => {
        this.componentes = [];
      });

    this.carregarPeriodos();
  }

  private carregarPeriodos(): void {
    this.carregandoPeriodos = true;
    let registrosIniciados = false;

    this.acompanhamentoService.getPeriodos(this.contexto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.carregandoPeriodos = false;
          if (!registrosIniciados) {
            // Observable completou sem periodos (ex: 403 → EMPTY do interceptor ou erro)
            this.carregandoInicialModal = false;
          }
        })
      )
      .subscribe((periodos) => {
        this.periodos = periodos;
        this.periodoOptions = periodos.map((p) => ({
          id: p.id,
          descricao: getDescricaoPeriodoOption(p),
          dataInicial: p.dataInicial,
          dataFinal: p.dataFinal,
          encerrado: p.encerrado
        }));
        this.periodoSelecionado = periodos.find((periodo) => periodo.ativo) || periodos[0] || null;
        registrosIniciados = true;
        this.carregarRegistros('inicial');
      }, () => {
        this.periodos = [];
        this.periodoOptions = [];
        this.periodoSelecionado = null;
      });
  }

  private carregarRegistros(tipo: 'inicial' | 'conteudo' = 'conteudo'): void {
    this.carregando = true;
    this.carregandoConteudoCard = tipo === 'conteudo';

    if (tipo === 'inicial') {
      this.carregandoInicialModal = true;
    }

    const dataFiltro = isDataFiltroValida(this.filtroData) ? this.filtroData : undefined;
    const filtros: AcompanhamentoFiltros = {
      termo: this.termoAluno.trim() || undefined,
      data: dataFiltro
    };

    // Na carga inicial buscamos todos os registros (sem filtro de período) para que
    // selecionarPeriodoComDadosQuandoNecessario consiga detectar qual período tem dados
    // e então filtramos localmente. Nas cargas subsequentes o período já está definido.
    const periodoIdParaBusca = tipo === 'inicial' ? undefined : this.periodoSelecionado?.id;
    const currentSearchId = ++this.searchId;

    this.acompanhamentoService.getRegistros(this.contexto, periodoIdParaBusca, filtros)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (currentSearchId === this.searchId) {
            this.carregando = false;
            this.carregandoInicialModal = false;
            this.carregandoConteudoCard = false;
          }
        })
      )
      .subscribe((registros) => {
        if (currentSearchId !== this.searchId) {
          return;
        }
        if (tipo === 'inicial') {
          this.selecionarPeriodoComDadosQuandoNecessario(registros);
          const periodoId = this.periodoSelecionado?.id;
          this.registrosOriginais = periodoId
            ? registros.filter((r) => r.periodoId === periodoId || !r.periodoId)
            : registros;
        } else {
          this.registrosOriginais = registros;
        }
        this.aplicarOrdenacao();
      }, () => {
        if (currentSearchId !== this.searchId) {
          return;
        }
        this.registrosOriginais = [];
        this.registros = [];
      });
  }

  private aplicarOrdenacao(): void {
    this.registros = aplicarOrdenacaoRegistros(this.registrosOriginais, this.ordenacaoSelecionada);
  }

  private carregarAlunosElegiveis(): void {
    if (this.semContexto || !this.formularioAberto) {
      return;
    }

    if (!this.formulario.componenteId) {
      this.alunos = [];
      this.sincronizarSelecaoAlunos();
      this.carregandoAlunos = false;
      return;
    }

    this.carregandoAlunos = true;
    const devePreservarSelecaoAtual = this.preservarSelecaoAlunosNoProximoCarregamento;
    const alunosSelecionadosAtuais = this.alunos.filter((aluno) => this.formulario.alunoIds.includes(aluno.id));
    // Também preserve alunos IDs que estão no formulário mas podem não estar em this.alunos ainda
    const alunoIdsParaPreservar = new Set(this.formulario.alunoIds);

    this.acompanhamentoService.getAlunosElegiveis(
      this.contexto,
      this.formulario.data || DateUtils.formatarDataISO(new Date()),
      this.formulario.componenteId,
      this.somentePublicoAlvo
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((alunos) => {
        const alunosAtivos = alunos.filter((aluno) => aluno.ativo);
        this.alunos = devePreservarSelecaoAtual
          ? mesclarAlunosSelecionados(alunosAtivos, alunosSelecionadosAtuais)
          : alunosAtivos;
        this._alunoIdsForçadosSelecionados = devePreservarSelecaoAtual ? alunoIdsParaPreservar : new Set<number>();
        this.preservarSelecaoAlunosNoProximoCarregamento = false;
        this.sincronizarSelecaoAlunos();
        this.carregandoAlunos = false;
      }, () => {
        this.alunos = alunosSelecionadosAtuais;
        this._alunoIdsForçadosSelecionados = alunoIdsParaPreservar;
        this.preservarSelecaoAlunosNoProximoCarregamento = false;
        this.sincronizarSelecaoAlunos();
        this.carregandoAlunos = false;
      });
  }

  private selecionarPeriodoComDadosQuandoNecessario(registros: AcompanhamentoPedagogicoRegistro[]): void {
    if (!this.periodos.length || !registros.length) {
      return;
    }

    const periodoAtualTemDados = !!this.periodoSelecionado?.id
      && registros.some((registro) => registro.periodoId === this.periodoSelecionado?.id);

    if (periodoAtualTemDados) {
      return;
    }

    const periodoComDados = this.periodos.find((periodo) => registros.some((registro) => registro.periodoId === periodo.id));
    if (periodoComDados) {
      this.periodoSelecionado = periodoComDados;
    }
  }

  isAlunoComDeficiencia(aluno: AcompanhamentoAluno): boolean {
    return !!aluno.possuiDeficienciaOuTranstorno;
  }

  sincronizarSelecaoAlunos(): void {
    const quantidadeAntes = this.formulario.alunoIds.length;
    const idsElegiveis = this.alunosElegiveis.map((aluno) => aluno.id);
    // Keep selected IDs even if they're not in the eligible list (forced selection during edit)
    this.formulario.alunoIds = this.formulario.alunoIds.filter(
      (id) => idsElegiveis.includes(id) || this._alunoIdsForçadosSelecionados.has(id)
    );
    this.destacarErroAlunosFormulario = quantidadeAntes > 0 && !this.formulario.alunoIds.length;
  }

  private abrirFormulario(formulario: AcompanhamentoFormulario, campoAdicional?: Record<string, unknown> | null): void {
    this.formularioAberto = true;
    this.mostrarErrosFormulario = false;
    this.destacarErroAlunosFormulario = false;
    this.carregandoCampoAdicional = false;
    this.campoAdicional = campoAdicional ? JSON.parse(JSON.stringify(campoAdicional)) : null;
    this.formulario = normalizarFormulario(formulario);
    this.preservarSelecaoAlunosNoProximoCarregamento = !!this.formulario.id;
    this.formularioSnapshotInicial = serializarFormulario(this.formulario);
    this.atualizarBloqueioScroll(true);
    this.carregarAlunosElegiveis();

    // Registro salvo: busca dados salvos via /{id}. Novo registro: busca template genérico.
    if (!this.campoAdicional) {
      this.carregandoCampoAdicional = true;

      if (formulario.id) {
        this.apiService.getCamposAdicionais(formulario.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (campos) => {
              this.campoAdicional = campos || null;
              this.carregandoCampoAdicional = false;
            },
            error: () => {
              this.carregandoCampoAdicional = false;
            }
          });
      } else {
        this.apiService.getCamposAdicionaisTemplate()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (campos) => {
              this.campoAdicional = campos || null;
              this.carregandoCampoAdicional = false;
            },
            error: () => {
              this.carregandoCampoAdicional = false;
            }
          });
      }
    }
  }

  private abrirConfirmacao(
    tipo: 'despublicar' | 'excluir',
    titulo: string,
    mensagem: string,
    registro: AcompanhamentoPedagogicoRegistro
  ): void {
    this.confirmacao = {
      aberta: true,
      tipo,
      titulo,
      mensagem,
      registro
    };
  }

  private confirmarExclusao(registro: AcompanhamentoPedagogicoRegistro): void {
    this.acompanhamentoService.excluirRegistro(this.contexto, registro.id, this.periodoSelecionado?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((registros) => {
        this.registrosOriginais = registros;
        this.registros = registros;
        this.notificationService.success({
          title: '',
          message: 'Acompanhamento pedagógico excluído com sucesso.',
          duration: 8000,
          position: 'top',
          hideTitle: true
        });
        this.fecharConfirmacao();
      });
  }

  private atualizarBloqueioScroll(bloquear: boolean): void {
    document.body.style.overflow = bloquear ? 'hidden' : '';
  }
}
